import { RequestContext } from '../../api';
import { accounting } from '../../api/accounting';
import { app } from '../../api/app';
import { calc } from '../../api/calc';
import { card } from '../../api/card';
import { communication } from '../../api/communication';
import { document } from '../../api/document';
import { infotrack } from '../../api/infotrack';
import { lawconnect } from '../../api/lawconnect';
import { matter } from '../../api/matter';
import { msteams } from '../../api/msteams';
import { person } from '../../api/person';
import { register } from '../../api/register';
import { schema } from '../../api/schema';
import { staff } from '../../api/staff';
import { system } from '../../api/system';
import { BaseApi } from '../base-impl';

import { Accounting } from './accounting';
import { App } from './app';
import { Calc } from './calc';
import { Card } from './card';
import { Communication } from './communication';
import { devDomains, jd, leapHostTestAppDomain, qeDomains, whitelistDomains } from './config/whitelistdomain';
import { Document } from './document';
import { Infotrack } from './infotrack';
import { LawConnect } from './lawconnect';
import { Matter } from './matter';
import { MsTeams } from './msteams';
import { Person } from './person';
import { Register } from './register';
import { Schema } from './schema';
import { Staff } from './staff';
import { System } from './system';

// mousemove fires very frequently; while the user is active, notify the host at most once every 10 minutes.
const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000;

export class IframeWindow extends BaseApi {
  private _whiteListedHostDomains: string[];
  private _lastHeartbeatSent = 0;

  private _accounting: accounting.Api;
  private _app: app.Api;
  private _calc: calc.Api;
  private _communication: communication.Api;
  private _document: document.Api;
  private _infotrack: infotrack.Api;
  private _lawconnect: lawconnect.Api;
  private _matter: matter.Api;
  private _msteams: msteams.Api;
  private _card: card.Api;
  private _staff: staff.Api;
  private _register: register.Api;
  private _person: person.Api;
  private _schema: schema.Api;
  private _system: system.Api;

  get accounting(): accounting.Api {
    return this._accounting;
  }
  get app(): app.Api {
    return this._app;
  }
  get calc(): calc.Api {
    return this._calc;
  }
  get card(): card.Api {
    return this._card;
  }
  get communication(): communication.Api {
    return this._communication;
  }
  get document(): document.Api {
    return this._document;
  }
  get infotrack(): infotrack.Api {
    return this._infotrack;
  }
  get lawconnect(): lawconnect.Api {
    return this._lawconnect;
  }
  get matter(): matter.Api {
    return this._matter;
  }
  get msteams(): msteams.Api {
    return this._msteams;
  }
  get staff(): staff.Api {
    return this._staff;
  }
  get person(): person.Api {
    return this._person;
  }
  get register(): register.Api {
    return this._register;
  }
  get schema(): schema.Api {
    return this._schema;
  }
  get system(): system.Api {
    return this._system;
  }

  private parentWindow: Window;

  constructor(requestContext: RequestContext, newTab: boolean = false) {
    super(requestContext);

    this._accounting = new Accounting(this);
    this._app = new App(this);
    this._calc = new Calc(this);
    this._card = new Card(this);
    this._communication = new Communication(this);
    this._document = new Document(this);
    this._infotrack = new Infotrack(this);
    this._lawconnect = new LawConnect(this);
    this._matter = new Matter(this);
    this._msteams = new MsTeams(this);
    this._person = new Person(this);
    this._register = new Register(this);
    this._schema = new Schema(this);
    this._staff = new Staff(this);
    this._system = new System(this);

    this.parentWindow = newTab ? window.opener : window.parent;
    this.registerMessageEventListener();
  }

  protected retrieveContext(): void {
    this.postMessage({
      command: 'RetrieveLeapAppContext',
      args: {
        requestId: this.leapContext.hostInfo.appSessionId,
      },
    });
  }

  protected hostRelatedSetup(): void {
    if (this.leapContext.hostInfo.hostType === 'web') {
      this._whiteListedHostDomains = [
        ...(whitelistDomains[`${this.leapContext.hostInfo.env}_${this.leapContext.hostInfo.region}`] || []),
        ...devDomains,
        ...qeDomains,
        leapHostTestAppDomain,
        jd,
      ];
    }
  }

  public internalSendAsync(request: any): Promise<any> {
    if (request === null || request === undefined) {
      throw new Error('Bad request');
    }

    const isObject = typeof request === 'object';

    if (!isObject) {
      console.error('Invalid request: request is not an object type');
      return null;
    }

    return this.twoWayCall<any, any>(
      request,
      (requestId, request) => {
        this.postMessage({
          command: 'system.send',
          args: { ...request, requestId },
        });
      },
      (response) => {
        return response;
      }
    );
  }

  public internalSend(request: any): void {
    this.postMessage({ command: 'system.send', args: request });
  }

  postMessage(message: { command: string; args?: any; sdkVersion?: number }): void {
    // Always append appSessionId to the message args sent to Host for validation
    message.args = {
      ...(message.args || {}),
    };

    if (this.leapContext?.hostInfo?.appSessionId) message.args.appSessionId = this.leapContext.hostInfo?.appSessionId;

    message.sdkVersion = message.sdkVersion ?? 2;
    this.parentWindow.postMessage(message, '*');
  }

  private registerMessageEventListener() {
    window.addEventListener('message', this.receiveMessage.bind(this), false);
  }

  private onMouseMove = () => {
    const now = Date.now();
    if (now - this._lastHeartbeatSent < HEARTBEAT_INTERVAL_MS) {
      return;
    }
    this._lastHeartbeatSent = now;
    this.postMessage({ command: 'internal.heartbeat' });
  };

  public addMouseMoveListener(): void {
    this._lastHeartbeatSent = 0;
    window.addEventListener('mousemove', this.onMouseMove, { passive: true });
  }

  private receiveMessage(event) {
    // Origin is not white-listed, ignore message
    // mobile doesn't have host
    if (this._whiteListedHostDomains?.length > 0 && !this._whiteListedHostDomains.includes(event.origin)) {
      return;
    }

    if (!!event.data.command) {
      const command: string = event.data.command;
      super.handleCommand(command, event.data.payload);
    }
  }
}
